import { useEffect, useMemo, useRef, useState } from "react";
import { Search, ArrowLeft, X, Plus } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchClubs } from "../../services/clubsApi";
import ClubCard from "../../components/Clubs/ClubCard";
import { useAuth } from "../../context/AuthContext";
import { useCreateClubModal } from "../../components/Clubs/createClub/CreateClubModalContext";

function useDebouncedValue(value, delay = 250) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function SkeletonClubCard() {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 overflow-hidden relative">
      <div className="absolute inset-0 opacity-40">
        <div className="absolute -top-10 -right-10 h-40 w-40 rounded-full bg-white/5 blur-2xl animate-pulse" />
      </div>

      <div className="relative flex items-center gap-3">
        <div className="h-12 w-12 rounded-xl bg-white/10 animate-pulse" />
        <div className="min-w-0 flex-1 space-y-2">
          <div className="h-4 w-2/3 bg-white/10 rounded animate-pulse" />
          <div className="h-3 w-1/2 bg-white/10 rounded animate-pulse" />
        </div>
      </div>
      <div className="relative mt-3 h-3 w-full bg-white/10 rounded animate-pulse" />
      <div className="relative mt-2 h-3 w-4/5 bg-white/10 rounded animate-pulse" />
    </div>
  );
}

function EmptyState({ onCreate, canCreate }) {
  return (
    <div className="mt-10 rounded-3xl border border-white/10 bg-white/[0.03] overflow-hidden relative">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-20 -left-16 h-72 w-72 rounded-full bg-emerald-400/10 blur-3xl animate-[pulse_3.5s_ease-in-out_infinite]" />
        <div className="absolute -bottom-24 -right-20 h-80 w-80 rounded-full bg-indigo-400/10 blur-3xl animate-[pulse_4.5s_ease-in-out_infinite]" />
      </div>

      <div className="relative px-5 py-7 sm:px-8 sm:py-10">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5 sm:gap-6">
          <div className="max-w-xl">
            <div className="text-white/90 text-base sm:text-xl font-semibold tracking-tight">
              No clubs found
            </div>
            <p className="mt-2 text-white/60 text-sm leading-relaxed">
              Try a different search, or start something new. Clubs are where people
              gather around a shared topic, post updates, and build a little corner
              of the community.
            </p>
          </div>

          <button
            type="button"
            onClick={onCreate}
            className="
              h-10 sm:h-11 px-4 sm:px-5 rounded-full
              border border-emerald-300/30
              bg-emerald-400/10
              text-white/90
              text-[10px] sm:text-[11px] tracking-[0.22em] uppercase
              inline-flex items-center justify-center gap-2
              transition
              hover:bg-emerald-400/14 hover:border-emerald-300/45
              focus:outline-none focus:ring-2 focus:ring-emerald-300/25
            "
            title={canCreate ? "Create a club" : "Log in to create a club"}
          >
            <Plus className="h-4 w-4 text-emerald-200/90" />
            Create Club
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ClubsDiscover() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { openCreateClubModal } = useCreateClubModal();

  const [q, setQ] = useState("");
  const debouncedQ = useDebouncedValue(q, 250);

  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const cacheRef = useRef(new Map());

  // Each page holds max 6 clubs
  const pageSize = 6;

  const cacheKey = useMemo(() => {
    const qKey = debouncedQ.trim().toLowerCase();
    return `clubs::${qKey}::${page}::${pageSize}`;
  }, [debouncedQ, page, pageSize]);

  const canPrev = page > 1;

  // Enable Next only if we received a full page
  const canNext = useMemo(() => {
    const cached = cacheRef.current.get(cacheKey);
    const len = Array.isArray(cached) ? cached.length : items.length;
    return len === pageSize; // if fewer than pageSize, we're at the end
  }, [cacheKey, items.length, pageSize]);

  const handleCreateClub = () => {
    if (!user) {
      navigate("/login", { state: { from: location.pathname } });
      return;
    }
    openCreateClubModal();
  };

  useEffect(() => setPage(1), [debouncedQ]);

  useEffect(() => {
    let alive = true;
    setErr("");

    const cached = cacheRef.current.get(cacheKey);
    if (cached) {
      setItems(cached);
      return;
    }

    setLoading(true);
    fetchClubs({ search: debouncedQ, page, pageSize })
      .then((data) => {
        if (!alive) return;
        const nextItems = data?.items ?? [];
        setItems(nextItems);
        cacheRef.current.set(cacheKey, nextItems);
      })
      .catch((e) => alive && setErr(e?.message || "Failed"))
      .finally(() => alive && setLoading(false));

    return () => {
      alive = false;
    };
  }, [cacheKey, debouncedQ, page, pageSize]);

  return (
    <div className="relative w-full min-h-screen overflow-hidden">
      {/* Lively ambient background (subtle, non-distracting) */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-24 -left-24 h-[420px] w-[420px] rounded-full bg-emerald-400/10 blur-3xl animate-[pulse_4.2s_ease-in-out_infinite]" />
        <div className="absolute top-1/3 -right-32 h-[520px] w-[520px] rounded-full bg-indigo-400/10 blur-3xl animate-[pulse_5.4s_ease-in-out_infinite]" />
        <div className="absolute -bottom-32 left-1/3 h-[560px] w-[560px] rounded-full bg-fuchsia-400/8 blur-3xl animate-[pulse_6.2s_ease-in-out_infinite]" />

        {/* film grain-ish overlay */}
        <div className="absolute inset-0 opacity-[0.06] bg-[radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.6)_1px,transparent_0)] [background-size:18px_18px]" />
      </div>

      {/* Centered frame so it never feels edge-to-edge */}
      <div className="relative w-full mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pt-5 sm:pt-10 pb-[max(90px,env(safe-area-inset-bottom))] sm:pb-12">
        {/* Top bar: Back + Title + Create */}
        <div className="flex items-center justify-between gap-2 sm:gap-3">
          {/* Keep the same Back button + label behavior (just smaller on mobile) */}
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="
              h-10 sm:h-11
              px-3 sm:px-4
              rounded-full
              border border-white/10
              bg-white/[0.03]
              backdrop-blur-md
              transition
              hover:bg-white/[0.05] hover:border-white/20
              text-[10px] sm:text-[11px]
              tracking-[0.20em] sm:tracking-[0.22em]
              uppercase
              text-white/85
              flex items-center gap-1.5 sm:gap-2
            "
            aria-label="Go back"
          >
            <ArrowLeft className="h-4 w-4 text-white/70" />
            <span className="hidden sm:inline">Back</span>
          </button>

          {/* Title block: mobile-optimized sizing without touching desktop */}
          <div className="flex items-center justify-center flex-1 min-w-0">
            <div className="relative text-center px-2 sm:px-0">
              <div className="absolute -left-10 top-1/2 h-px w-8 bg-white/10 hidden sm:block" />
              <div className="absolute -right-10 top-1/2 h-px w-8 bg-white/10 hidden sm:block" />

              <h1 className="text-white text-2xl sm:text-4xl font-semibold tracking-tight">
                Clubs
              </h1>
              <p className="mt-1 text-[11px] sm:text-[12px] text-white/55 leading-snug">
                Find your people. Build your corner of the community.
              </p>
            </div>
          </div>

          {/* Keep the same Create button + label behavior (just smaller on mobile) */}
          <button
            type="button"
            onClick={handleCreateClub}
            className="
              h-10 sm:h-11
              px-3 sm:px-5
              rounded-full
              border border-emerald-300/25
              bg-emerald-400/10
              backdrop-blur-md
              transition
              hover:bg-emerald-400/14 hover:border-emerald-300/40
              text-[10px] sm:text-[11px]
              tracking-[0.20em] sm:tracking-[0.22em]
              uppercase
              text-white/90
              flex items-center gap-1.5 sm:gap-2
              focus:outline-none focus:ring-2 focus:ring-emerald-300/25
            "
            aria-label="Create a club"
            title={user ? "Create a club" : "Log in to create a club"}
          >
            <Plus className="h-4 w-4 text-emerald-200/90" />
            <span className="hidden sm:inline">Create</span>
          </button>
        </div>

        <div className="mt-4 sm:mt-6 h-px w-full bg-white/10" />

        {/* Search row */}
        <div className="mt-4 sm:mt-5 flex justify-center">
          <div
            className="
              w-full sm:w-[820px]
              h-11
              rounded-full
              border border-white/10
              bg-white/[0.035]
              backdrop-blur-xl
              px-4
              flex items-center gap-2.5
              transition
              hover:border-white/20 hover:bg-white/[0.05]
              focus-within:border-emerald-300/35 focus-within:bg-white/[0.055]
            "
          >
            <Search className="h-4.5 w-4.5 text-white/60 shrink-0" />

            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search clubs…"
              aria-label="Search clubs"
              className="
                w-full min-w-0
                bg-transparent
                text-[13px] text-white/90
                placeholder:text-white/40
                appearance-none
                border-0 outline-none
                ring-0 focus:ring-0
                shadow-none focus:shadow-none
              "
            />

            {q ? (
              <button
                type="button"
                onClick={() => setQ("")}
                className="
                  h-8 w-8 grid place-items-center
                  rounded-full
                  text-white/60 hover:text-white/90
                  hover:bg-white/5
                  transition
                "
                aria-label="Clear search"
                title="Clear"
              >
                <X className="h-4 w-4" />
              </button>
            ) : null}
          </div>
        </div>

        {/* Error */}
        {err ? (
          <div className="mt-6 sm:mt-7 rounded-3xl border border-red-400/20 bg-red-500/10 px-5 py-4 text-red-200 text-sm">
            {err}
          </div>
        ) : null}

        {/* Grid (max 6 per page)
             Mobile: true single-column stack, nice spacing, centered max width
             Desktop styles unchanged (still sm:2 cols, lg:3 cols) */}
        <div className="mt-6 sm:mt-8">
          <div className="mx-auto w-full max-w-[520px] sm:max-w-none">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
              {loading && items.length === 0
                ? Array.from({ length: pageSize }).map((_, i) => (
                    <SkeletonClubCard key={i} />
                  ))
                : items.map((c) => (
                    <div key={c?.id ?? c?.slug} className="w-full">
                      {/* Mobile polish wrapper without affecting desktop layout */}
                      <div className="rounded-2xl sm:rounded-none">
                        <ClubCard club={c} />
                      </div>
                    </div>
                  ))}
            </div>
          </div>
        </div>

        {/* Empty */}
        {!loading && !err && items.length === 0 ? (
          <EmptyState onCreate={handleCreateClub} canCreate={!!user} />
        ) : null}

        {/* Pagination
             Desktop unchanged
             Mobile: fixed bottom bar so it feels app-native + easy thumb reach */}
        <div className="mt-10 flex items-center justify-center gap-3 hidden sm:flex">
          <button
            onClick={() => canPrev && setPage((p) => Math.max(1, p - 1))}
            disabled={!canPrev}
            className="
              h-10 px-4 rounded-full
              border border-white/10
              bg-white/[0.03]
              text-[11px] tracking-[0.18em] uppercase
              text-white/80
              transition
              hover:border-white/20 hover:bg-white/[0.05]
              disabled:opacity-40 disabled:hover:bg-white/[0.03] disabled:hover:border-white/10
            "
          >
            Prev
          </button>

          <div className="h-10 px-4 rounded-full border border-white/10 bg-white/[0.03] flex items-center text-[11px] tracking-[0.18em] uppercase text-white/55">
            Page <span className="ml-2 text-white/85 tracking-normal">{page}</span>
          </div>

          <button
            onClick={() => canNext && setPage((p) => p + 1)}
            disabled={!canNext}
            className="
              h-10 px-4 rounded-full
              border border-white/10
              bg-white/[0.03]
              text-[11px] tracking-[0.18em] uppercase
              text-white/80
              transition
              hover:border-white/20 hover:bg-white/[0.05]
              disabled:opacity-40 disabled:hover:bg-white/[0.03] disabled:hover:border-white/10
            "
          >
            Next
          </button>
        </div>

        {/* Mobile pagination bar */}
        <div className="fixed inset-x-0 bottom-0 sm:hidden z-40">
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
          <div className="relative px-4 pb-[max(12px,env(safe-area-inset-bottom))] pt-3">
            <div
              className="
                mx-auto w-full max-w-[520px]
                rounded-2xl
                border border-white/10
                bg-white/[0.045]
                backdrop-blur-xl
                px-3 py-3
                flex items-center justify-between gap-3
              "
            >
              <button
                onClick={() => canPrev && setPage((p) => Math.max(1, p - 1))}
                disabled={!canPrev}
                className="
                  h-10 px-4 rounded-full
                  border border-white/10
                  bg-white/[0.03]
                  text-[10px] tracking-[0.18em] uppercase
                  text-white/85
                  transition
                  disabled:opacity-40
                "
              >
                Prev
              </button>

              <div className="h-10 px-4 rounded-full border border-white/10 bg-white/[0.03] flex items-center text-[10px] tracking-[0.18em] uppercase text-white/55">
                Page <span className="ml-2 text-white/85 tracking-normal">{page}</span>
              </div>

              <button
                onClick={() => canNext && setPage((p) => p + 1)}
                disabled={!canNext}
                className="
                  h-10 px-4 rounded-full
                  border border-white/10
                  bg-white/[0.03]
                  text-[10px] tracking-[0.18em] uppercase
                  text-white/85
                  transition
                  disabled:opacity-40
                "
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
